import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import { useAuth } from "../../context/AuthContext";
import {
  addReviewFollowupNote,
  getReviewFollowup,
  getReviewFollowups,
  updateReviewFollowup,
} from "../../api/clientReviews";
import { listSalonUsers } from "../../api/salonUsers";
import { getSalonTimezone } from "../../utils/salonTimezone";
import {
  formatDateInTimeZone,
  formatDateTimeInTimeZone,
  formatInTimeZone,
  parseLocalDateTimeInTimeZone,
} from "../../utils/datetime";

const STATUS_TABS = [
  { label: "New", value: "new" },
  { label: "Contacted", value: "contacted" },
  { label: "Resolving", value: "resolving" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

const PRIORITY_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Normal", value: "normal" },
  { label: "High", value: "high" },
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTimeISO = (value, timeZone) =>
  formatInTimeZone(
    value,
    timeZone,
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    "en-GB"
  );

const renderStars = (value) => {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) {
    return <span className="text-xs text-slate-400">--</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, idx) => (
        <span
          key={idx}
          className={`h-2.5 w-2.5 rounded-full ${
            idx < rating ? "bg-amber-400" : "bg-slate-200"
          }`}
        />
      ))}
      <span className="text-xs text-slate-500">{rating}/5</span>
    </div>
  );
};

export default function ReviewFollowups() {
  const navigate = useNavigate();
  const { currentSalon, user } = useAuth();
  const salonId =
    currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const [statusFilter, setStatusFilter] = useState("new");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState({
    status: "new",
    priority: "normal",
    assigned_to_user_id: "",
    due_date: "",
    due_time: "09:00",
    internal_notes: "",
  });

  const loadUsers = useCallback(async () => {
    try {
      const data = await listSalonUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setUsers([]);
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getReviewFollowups({
        salon_id: salonId,
        status: statusFilter,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load follow-ups.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [salonId, statusFilter]);

  const loadDetail = useCallback(
    async (followupId) => {
      if (!followupId) return;
      setDetailLoading(true);
      setDetailError("");
      try {
        const data = await getReviewFollowup(followupId);
        setDetail(data || null);
        if (data) {
          const dueDate = data?.due_at
            ? formatDateISO(new Date(data.due_at), salonTimezone)
            : "";
          const dueTime = data?.due_at
            ? formatTimeISO(data.due_at, salonTimezone)
            : "09:00";
          setDraft({
            status: data.status || "new",
            priority: data.priority || "normal",
            assigned_to_user_id: data.assigned_to_user_id || "",
            due_date: dueDate,
            due_time: dueTime || "09:00",
            internal_notes: data.internal_notes || "",
          });
        }
      } catch (err) {
        setDetailError(
          err?.response?.data?.detail || "Unable to load follow-up details."
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [salonTimezone]
  );

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleSelect = (row) => {
    setSelectedId(row?.id || null);
  };

  const handleSave = async () => {
    if (!detail?.id || saving) return;
    setSaving(true);
    setDetailError("");
    try {
      const payload = {
        status: draft.status,
        priority: draft.priority,
        assigned_to_user_id: draft.assigned_to_user_id || null,
        internal_notes: draft.internal_notes?.trim() || null,
      };
      if (draft.due_date) {
        const dueUtc = parseLocalDateTimeInTimeZone(
          `${draft.due_date} ${draft.due_time || "09:00"}`,
          salonTimezone
        );
        payload.due_at = dueUtc ? dueUtc.toISOString() : null;
      } else {
        payload.due_at = null;
      }
      const updated = await updateReviewFollowup(detail.id, payload);
      setDetail(updated || detail);
      await loadRows();
    } catch (err) {
      setDetailError(
        err?.response?.data?.detail || "Unable to update follow-up."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!detail?.id || noteSaving || !noteDraft.trim()) return;
    setNoteSaving(true);
    setDetailError("");
    try {
      await addReviewFollowupNote(detail.id, { note: noteDraft.trim() });
      setNoteDraft("");
      await loadDetail(detail.id);
    } catch (err) {
      setDetailError(
        err?.response?.data?.detail || "Unable to add note."
      );
    } finally {
      setNoteSaving(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/schedule");
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Client Follow-ups
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6 text-sm md:text-base">
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setStatusFilter(tab.value)}
                  className={`rounded-full px-4 py-2 text-xs md:text-sm font-semibold transition ${
                    statusFilter === tab.value
                      ? "bg-rose-500 text-white"
                      : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-6">
              <div className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Follow-up queue
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Created</th>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Ticket</th>
                        <th className="px-3 py-2 text-left">Technician</th>
                        <th className="px-3 py-2 text-left">Overall</th>
                        <th className="px-3 py-2 text-left">Assigned to</th>
                        <th className="px-3 py-2 text-left">Due</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Priority</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {loading && (
                        <tr>
                          <td colSpan={9} className="px-4 py-4 text-slate-500">
                            Loading follow-ups...
                          </td>
                        </tr>
                      )}
                      {!loading && rows.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-4 py-4 text-slate-500">
                            No follow-ups found.
                          </td>
                        </tr>
                      )}
                      {rows.map((row) => (
                        <tr
                          key={row.id}
                          className={`cursor-pointer hover:bg-slate-50 ${
                            selectedId === row.id ? "bg-rose-50/60" : ""
                          }`}
                          onClick={() => handleSelect(row)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                            {formatDateInTimeZone(row.created_at, salonTimezone)}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.client_name || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.ticket_number || row.ticket_id}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.technician_name || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.overall_rating ? `${row.overall_rating}/5` : "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.assigned_to_name || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.due_at
                              ? formatDateInTimeZone(row.due_at, salonTimezone)
                              : "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700 capitalize">
                            {row.status}
                          </td>
                          <td className="px-3 py-2 text-slate-700 capitalize">
                            {row.priority}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Detail
                </div>
                {!selectedId && (
                  <div className="text-sm text-slate-500">
                    Select a follow-up to view details.
                  </div>
                )}
                {detailLoading && (
                  <div className="text-sm text-slate-500">Loading details...</div>
                )}
                {detailError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {detailError}
                  </div>
                )}
                {detail && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 space-y-1">
                      <div className="text-sm font-semibold text-slate-900">
                        {detail.client_name || "Client"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Ticket #{detail.ticket_number || detail.ticket_id}
                      </div>
                      {detail.client_phone && (
                        <div className="text-xs text-slate-500">
                          Call client:{" "}
                          <a
                            href={`tel:${detail.client_phone}`}
                            className="font-semibold text-rose-600 hover:text-rose-700"
                          >
                            {detail.client_phone}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Overall
                        </div>
                        {renderStars(detail.overall_rating)}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Salon
                        </div>
                        {renderStars(detail.salon_rating)}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Reception
                        </div>
                        {renderStars(detail.reception_rating)}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Service
                        </div>
                        {renderStars(detail.service_rating)}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Staff
                        </div>
                        {renderStars(detail.staff_rating)}
                      </div>
                      {detail.online_booking_rating != null && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Online booking
                          </div>
                          {renderStars(detail.online_booking_rating)}
                        </div>
                      )}
                    </div>
                    {Array.isArray(detail.technician_ratings) &&
                      detail.technician_ratings.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Technician ratings
                          </div>
                          <div className="space-y-2">
                            {detail.technician_ratings.map((rating) => (
                              <div
                                key={rating.technician_user_id}
                                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                              >
                                <div className="font-semibold text-slate-800">
                                  {rating.technician_name ||
                                    `Technician #${rating.technician_user_id}`}
                                </div>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Service
                                    </div>
                                    {renderStars(rating.service_rating)}
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Staff
                                    </div>
                                    {renderStars(rating.staff_rating)}
                                  </div>
                                </div>
                                {rating.comment && (
                                  <div className="mt-2 text-xs text-slate-600 whitespace-pre-line">
                                    {rating.comment}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    {detail.comment && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 whitespace-pre-line">
                        {detail.comment}
                      </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Update
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Status
                          </label>
                          <select
                            value={draft.status}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                status: e.target.value,
                              }))
                            }
                            className="h-10 w-full rounded-xl border border-slate-300 px-3"
                          >
                            {STATUS_TABS.map((tab) => (
                              <option key={tab.value} value={tab.value}>
                                {tab.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Priority
                          </label>
                          <select
                            value={draft.priority}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                priority: e.target.value,
                              }))
                            }
                            className="h-10 w-full rounded-xl border border-slate-300 px-3"
                          >
                            {PRIORITY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Assigned to
                          </label>
                          <select
                            value={draft.assigned_to_user_id || ""}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                assigned_to_user_id: e.target.value,
                              }))
                            }
                            className="h-10 w-full rounded-xl border border-slate-300 px-3"
                          >
                            <option value="">Unassigned</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.full_name || user.username || user.email}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Due date
                            </label>
                            <input
                              type="date"
                              value={draft.due_date}
                              onChange={(e) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  due_date: e.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 px-3"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Due time
                            </label>
                            <input
                              type="time"
                              value={draft.due_time}
                              onChange={(e) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  due_time: e.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 px-3"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Internal notes
                          </label>
                          <textarea
                            value={draft.internal_notes || ""}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                internal_notes: e.target.value,
                              }))
                            }
                            rows={3}
                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={saving}
                          className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                        >
                          {saving ? "Saving..." : "Save changes"}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Notes
                      </div>
                      <div className="space-y-2 text-sm text-slate-700 max-h-48 overflow-y-auto">
                        {detail.notes?.length ? (
                          detail.notes.map((note) => (
                            <div
                              key={note.id}
                              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <div className="text-xs text-slate-500">
                                {note.author_name || "Staff"} -{" "}
                                {formatDateTimeInTimeZone(
                                  note.created_at,
                                  salonTimezone
                                )}
                              </div>
                              <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">
                                {note.note}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-slate-500">
                            No notes yet.
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <textarea
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          rows={3}
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Add a note..."
                        />
                        <button
                          type="button"
                          onClick={handleAddNote}
                          disabled={noteSaving || !noteDraft.trim()}
                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        >
                          {noteSaving ? "Saving..." : "Add note"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
